import { Component, OnInit, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { MatDialogModule } from "@angular/material/dialog";
import {
  HardBtnComponent,
  HardFormFieldTypeEnum,
  HardFormModule,
  HardModalComponent,
  HardTranslatePipe,
  IHardFormField,
} from "@app/shared/ui-lib/src/lib";
import { IHardFormSelectParams } from "@app/shared/ui-lib/src/lib/hard-form/interfaces/hard-form-field-params";
import { BaseModalComponent } from "@app/shared/modals/base-modal/base-modal.component";
import {
  IFleetPermission,
  IFleetPermissionModalData,
} from "@app/shared/data-access/src/lib/resource-management/models/fleet";
import { ResourceManagementEndpoints } from "@app/shared/data-access/src/lib/resource-management/types/resource-management-endpoints";
import { DictionaryService } from "@dmms/core/http";
import { IDicBase } from "@models/dictionary/dic-base";
import { IApiResponse } from "@models/http/http-options";

const EXPIRES_TYPE_INDEFINITE = "indefinite";

const DEFAULT_OWNER_TYPE: IDicBase = {
  id: "fcd32fe5-6617-4ac1-2aef-08debc81f45f",
  code: "OWNER",
  name: {
    ru: "Владелец",
    kz: "Иесі",
    en: "Owner",
  },
};

@Component({
  selector: "app-fleet-permission-modal",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    HardModalComponent,
    HardBtnComponent,
    HardFormModule,
    HardTranslatePipe,
  ],
  templateUrl: "./fleet-permission-modal.component.html",
  styleUrl: "./fleet-permission-modal.component.scss",
})
export class FleetPermissionModalComponent
  extends BaseModalComponent<
    FleetPermissionModalComponent,
    IFleetPermissionModalData
  >
  implements OnInit
{
  readonly types = HardFormFieldTypeEnum;

  modalForm: FormGroup;
  gridFields: IHardFormField[] = [];

  private readonly dicService = inject(DictionaryService);

  get isEditing(): boolean {
    return this.data.editingIndex != null;
  }

  constructor(private readonly fb: FormBuilder) {
    super();
  }

  ngOnInit(): void {
    const permission = this.data.permission;

    this.modalForm = this.fb.group({
      user: [permission?.user ?? null, Validators.required],
      fleetManageType: [
        {
          value: permission?.fleetManageType ?? DEFAULT_OWNER_TYPE,
          disabled: true,
        },
        Validators.required,
      ],
      expiresType: [
        {
          value: EXPIRES_TYPE_INDEFINITE,
          disabled: true,
        },
        Validators.required,
      ],
      expiresAt: [
        {
          value: null,
          disabled: true,
        },
      ],
    });

    this.buildGridFields();
    this.loadOwnerManageType(permission?.fleetManageType);
  }

  private loadOwnerManageType(existingType?: IDicBase): void {
    this.dicService
      .getAll<IApiResponse<IDicBase[]>>(
        ResourceManagementEndpoints.FleetManageType,
      )
      .subscribe({
        next: (res) => {
          const types = res?.value ?? [];
          const ownerType =
            types.find((t) => t.code?.toUpperCase() === "OWNER") ??
            existingType ??
            DEFAULT_OWNER_TYPE;

          this.modalForm.get("fleetManageType")?.setValue(ownerType);
        },
        error: () => {
          if (!this.modalForm.get("fleetManageType")?.value) {
            this.modalForm
              .get("fleetManageType")
              ?.setValue(existingType ?? DEFAULT_OWNER_TYPE);
          }
        },
      });
  }

  private buildGridFields(): void {
    this.gridFields = [
      {
        name: "user",
        title: "dicTable.selectUser",
        type: HardFormFieldTypeEnum.Dictionary,
        params: {
          dicType: this.data.userDicType,
          fieldNames: ["fullName"],
          subNames: ["email"],
          validators: [Validators.required],
        } as IHardFormSelectParams,
      },
    ];
  }

  save(): void {
    if (this.modalForm.invalid) return;

    const val = this.modalForm.getRawValue();
    const permission: IFleetPermission = {
      fleetManageType: val.fleetManageType,
      fleetManageTypeId: val.fleetManageType?.id,
      user: val.user,
      userId: val.user?.id,
      expiresAt: null,
    };

    this.close({ permission, editingIndex: this.data.editingIndex ?? null });
  }
}
